import { useState } from 'react';

import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { cn } from '@/lib/utils';

import { selectThumbsDown, selectThumbsUp } from '../../../redux/selectors';

type ReactionItem = {
  request_id?: string;
  reaction?: string;
  firstMessageText?: string;
  nodeIndex: number;
};

type BlockProps = { label: string; items: ReactionItem[] };

const ReactionBlock = ({ label, items }: BlockProps) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex items-center gap-1 self-start text-xs leading-4 font-medium text-foreground-muted hover:text-foreground"
      >
        <span>{label}</span>
        <ChevronDownIcon size={16} className={cn('transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <ul className="flex flex-col gap-2 pl-0.5">
          {items.map((item) => (
            <li
              key={item.request_id ?? item.nodeIndex}
              className="border-l border-stroke pl-3 text-sm leading-5 font-normal wrap-anywhere text-foreground-muted"
            >
              {item.firstMessageText}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const ReactionsList = () => {
  const { t } = useTranslation();
  const up = useSelector(selectThumbsUp);
  const down = useSelector(selectThumbsDown);

  if (up.length === 0 && down.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {down.length > 0 && (
        <ReactionBlock
          label={t('try_it.review.negative_reactions', { 0: down.length })}
          items={down}
        />
      )}
      {up.length > 0 && (
        <ReactionBlock label={t('try_it.review.positive_reactions', { 0: up.length })} items={up} />
      )}
    </div>
  );
};
